import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { IClass } from "../Classes";
import { ActionType, IPagination, Order } from "../common/Interfaces";
import { getComparator } from "../common/utils";

export interface IStudent {
  id: number;
  studentId: string;
  name: string;
  grade: string;
  gradeId: number;
  className: string;
  classId: number;
  password: string;
  email?: string;
  code: string;
  selected: boolean;
}

interface IStudentEntities {
  [key: number]: IStudent;
}
export enum StudentsFilterTypes {
  StudentId = "StudentId",
  Student = "Student",
}
export enum StudentsColumns {
  StudentId = "StudentId",
  Student = "Student",
  Email = "Email",
  // Grade = "Grade",
  // Class = "Class",
}

export enum StudentsActionLabels {
  SharePassword = "SharePassword",
  Password = "Password",
  Edit = "Edit",
  Delete = "Delete",
}

interface IStudentsState {
  entities: IStudentEntities;
  pagination: IPagination;
  actionMode: ActionType;
  filterType: StudentsFilterTypes;
  searchText: string;
  order: Order;
  orderBy: StudentsColumns;
  classInfo: IClass | null;
  updateItem: IStudent | null;
  deleteItem: IStudent | null;
  selectAll: boolean;
}

const initialState: IStudentsState = {
  entities: {},
  pagination: {
    currentPage: 1,
    totalPages: 1,
    pageSize: 15,
  },
  actionMode: ActionType.list,
  filterType: StudentsFilterTypes.Student,
  searchText: "",
  order: "asc",
  orderBy: StudentsColumns.Student,
  classInfo: null,
  updateItem: null,
  deleteItem: null,
  selectAll: false,
};
export const studentsSlice = createSlice({
  name: "students",
  initialState,
  reducers: {
    studentsLoaded(state: IStudentsState, action: PayloadAction<IStudent[]>) {
      state.entities = action.payload.reduce<IStudentEntities>(
        (entities, item) => {
          entities[item.id] = item;
          return entities;
        },
        {}
      );
      state.pagination.totalPages = Math.ceil(
        action.payload.length / state.pagination.pageSize
      );
    },
    updateStudents(state: IStudentsState, action: PayloadAction<IStudent>) {
      state.entities[action.payload.id] = action.payload;
    },
    currentPageSet(state: IStudentsState, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload;
    },
    totalPagesSet(state: IStudentsState, action: PayloadAction<number>) {
      state.pagination.totalPages = action.payload;
    },
    actionModeSet(state: IStudentsState, action: PayloadAction<ActionType>) {
      state.actionMode = action.payload;
    },
    filterSet(
      state: IStudentsState,
      action: PayloadAction<StudentsFilterTypes>
    ) {
      state.filterType = action.payload;
    },
    searchTextSet(state: IStudentsState, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    orderSet(state: IStudentsState, action: PayloadAction<Order>) {
      state.order = action.payload;
    },
    orderBySet(state: IStudentsState, action: PayloadAction<StudentsColumns>) {
      state.orderBy = action.payload;
    },
    classInfoSet(state: IStudentsState, action: PayloadAction<IClass>) {
      state.classInfo = action.payload;
    },
    updateItemSet(
      state: IStudentsState,
      action: PayloadAction<IStudent | null>
    ) {
      state.updateItem = action.payload;
    },
    deleteItemSet(
      state: IStudentsState,
      action: PayloadAction<IStudent | null>
    ) {
      state.deleteItem = action.payload;
    },
    selectAllSet(state: IStudentsState, action: PayloadAction<boolean>) {
      state.selectAll = action.payload;
    },
  },
});

export const selectOrder = (state: RootState) => state.students.order;
export const selectOrderBy = (state: RootState) => state.students.orderBy;
export const selectUpdateItem = (state: RootState) => state.students.updateItem;
export const selectDeleteItem = (state: RootState) => state.students.deleteItem;
export const selectClassInfo = (state: RootState) => state.students.classInfo;
export const selectSelectAll = (state: RootState) => state.students.selectAll;
export const selectStudentEntities = (state: RootState) =>
  state.students.entities;
export const selectStudents = createSelector(
  selectStudentEntities,
  (entities) => Object.values(entities) as IStudent[]
);
export const selectFilterType = (state: RootState) => state.students.filterType;
export const selectSearchText = (state: RootState) => state.students.searchText;
export const selectFilteredStudents = createSelector(
  selectStudents,
  selectFilterType,
  selectSearchText,
  (students, filter, text) => {
    const list = students.filter((item) => {
      let searchIndex: string =
        StudentsFilterTypes[
          filter as keyof typeof StudentsFilterTypes
        ].toLowerCase();
      if (searchIndex === "student") searchIndex = "name";
      if (searchIndex === "studentid") searchIndex = "studentId";
      const result = text
        ? item[searchIndex as keyof IStudent]
            ?.toString()
            .toLowerCase()
            .includes(text.toLowerCase())
        : true;
      return result;
    });
    return list;
  }
);
export const selectStudentsIDs = createSelector(selectStudents, (students) =>
  students.map((item) => item.id)
);
export const selectStudentById = (state: RootState, studentId: number) =>
  state.students.entities[studentId];

export const selectStudentsPagination = (state: RootState): IPagination =>
  state.students.pagination;

export const currentPageStudentList = createSelector(
  selectFilteredStudents,
  selectOrder,
  selectOrderBy,
  selectStudentsPagination,
  (list, order, orderBy, pagination: IPagination) => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const sortedList = (list as any[])
      .sort(
        getComparator<IStudent>(
          order,
          (orderBy === "Student"
            ? "name"
            : orderBy === "StudentId"
            ? "studentId"
            : orderBy.toLowerCase()) as keyof IStudent
        )
      )
      .slice(startIndex, endIndex);
    return sortedList;
  }
);

export const selectStudentsActionMode = (state: RootState): ActionType =>
  state.students.actionMode;

export const {
  studentsLoaded,
  currentPageSet,
  totalPagesSet,
  actionModeSet,
  filterSet,
  searchTextSet,
  orderBySet,
  orderSet,
  classInfoSet,
  updateItemSet,
  deleteItemSet,
  selectAllSet,
  updateStudents,
} = studentsSlice.actions;

export default studentsSlice.reducer;
